import { RavenSelect } from "@ravenappdev/raven-ui";
import type { RavenSelectOption } from "@ravenappdev/raven-ui";
import type { TextFieldProps } from "@mui/material/TextField";

export type { RavenSelectOption };

export interface SelectProps
  extends Omit<TextFieldProps, "onChange" | "value" | "select" | "children"> {
  options: RavenSelectOption[];
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  fullWidth?: boolean;
}

export default function Select({
  options,
  label,
  value,
  onChange,
  fullWidth = true,
  size = "small",
  slotProps,
  ...rest
}: SelectProps) {
  const effectiveValue = value ?? "";
  // An option whose value is "" (a leading "All …" row) is a real choice, so
  // the Select must render it rather than an empty box — and MUI keeps the
  // label unshrunk for an empty value, which would then sit on top of it.
  // `displayEmpty` + an explicit `shrink` + an explicit `notched` is the
  // documented trio; without the last one the floating label crosses an
  // un-notched outline.
  const displayEmpty = options.some((o) => o.value === "");
  const shrink = displayEmpty || Boolean(effectiveValue);

  return (
    <RavenSelect
      options={options}
      label={label}
      value={effectiveValue}
      onChange={(e) => onChange?.(e.target.value)}
      fullWidth={fullWidth}
      size={size}
      slotProps={{
        ...slotProps,
        select: { displayEmpty, ...slotProps?.select },
        inputLabel: { shrink, ...slotProps?.inputLabel },
        input: { notched: shrink, ...slotProps?.input },
      }}
      {...rest}
    />
  );
}
